import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';

import { useAuth } from '@/lib/auth/useAuth';
import { showJoinCta } from '@/lib/nav/roleNav';
import { freemiumQueries } from '@/lib/queries/freemium';

// Spec 02 — the single "Join" membership CTA shared by both chromes (the browse
// video TopBar and the subscriber editorial AppShell) so the word, destination,
// and visibility logic can't drift between them.
//
// Visibility is server-derived, reusing the same freemiumQueries.status the
// browse TopBar already calls (user_has_active_subscription): shown to anon +
// signed-in free, hidden for active members. Anon → query disabled → status
// undefined → not a member → shown; member → hidden everywhere.
//
// NON-GOAL: "Join" = membership only. It routes to /subscribe and never toward
// creator access — creator levels are invite-only and entirely out of scope.
// Nothing here introduces or implies a reader→creator path.

const surfaceClass: Record<'video' | 'editorial', string> = {
  // Browse: the single oxblood-filled conversion button (Spec 30 H2), hovering
  // to the brighter oxblood-live.
  video: 'bg-oxblood-browse text-video-text hover:bg-oxblood-live',
  // Subscriber shell: editorial oxblood/parchment tokens.
  editorial: 'bg-oxblood text-on-accent hover:bg-oxblood/90',
};

export function JoinCta({ surface }: { surface: 'video' | 'editorial' }) {
  const { session, profile } = useAuth();
  const signedIn = Boolean(session);
  const { data: status } = useQuery(freemiumQueries.status(signedIn));
  // Suppression is centralized in showJoinCta (Spec 27): hidden for members AND
  // for staff / approved contributors (for whom "Become a member" is a category
  // error). The server-derived membership check still gates the member case.
  // Anon (profile null, status undefined) + free signed-in reader → shown.
  if (!showJoinCta(profile, status?.isMember ?? false)) return null;
  return (
    <Link
      to="/subscribe"
      className={`rounded-md px-4 py-2 font-sans text-sm font-medium transition ${surfaceClass[surface]}`}
    >
      Become a member
    </Link>
  );
}
